import { SandboxDomainError, SandboxStore } from '../../store';
import {
  createLoyaltySeed,
  SandboxAchievement,
  SandboxCashback,
  SandboxDiscount,
  SandboxLoyaltySeed,
  SandboxPromoCampaign,
  SandboxPromoClientScope,
  SandboxPromoCode,
  SandboxPromoValue,
} from '../../store/seed/loyalty';
import { SandboxOperationHandler, SandboxVariables } from '../types';
import { inputObject } from './utils';

interface LoyaltyRuntime extends SandboxLoyaltySeed {
  anchorMs: number;
  nextDiscountId: number;
  nextCashbackId: number;
  nextAchievementId: number;
  nextPromoCodeId: number;
  nextPromoCampaignId: number;
}

const runtimes = new WeakMap<SandboxStore, LoyaltyRuntime>();
const presetDeleteError =
  'Предустановленные сущности нельзя удалить в локальной песочнице';

const createRuntime = (store: SandboxStore): LoyaltyRuntime => {
  const anchorMs = store.getAnchorMs();
  const seed = createLoyaltySeed(anchorMs);
  return {
    anchorMs,
    ...seed,
    nextDiscountId: Math.max(...seed.discounts.map(({ id }) => id)) + 1,
    nextCashbackId: Math.max(...seed.cashbacks.map(({ id }) => id)) + 1,
    nextAchievementId: Math.max(...seed.achievements.map(({ id }) => id)) + 1,
    nextPromoCodeId: Math.max(...seed.promoCodes.map(({ id }) => id)) + 1,
    nextPromoCampaignId: seed.promoCampaigns.length + 1,
  };
};

const runtime = (store: SandboxStore): LoyaltyRuntime => {
  const current = runtimes.get(store);
  if (current?.anchorMs === store.getAnchorMs()) return current;
  const next = createRuntime(store);
  runtimes.set(store, next);
  return next;
};

const recordValue = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const numberId = (value: unknown, entity: string): number => {
  const id = Number(value);
  if (!Number.isInteger(id) || id <= 0) {
    throw new SandboxDomainError(`${entity}: некорректный идентификатор`);
  }
  return id;
};

const titleValue = (value: unknown): string => {
  const title = String(value ?? '').trim();
  if (title.length < 2) {
    throw new SandboxDomainError('Название должно содержать минимум 2 символа');
  }
  return title;
};

const assertRuntimeDelete = (entity: { origin: string }): void => {
  if (entity.origin === 'preset')
    throw new SandboxDomainError(presetDeleteError);
};

const discountById = (
  state: LoyaltyRuntime,
  value: unknown
): SandboxDiscount => {
  const id = numberId(value, 'Скидка');
  const discount = state.discounts.find((item) => item.id === id);
  if (!discount) throw new SandboxDomainError('Скидка не найдена');
  return discount;
};

const cashbackById = (
  state: LoyaltyRuntime,
  value: unknown
): SandboxCashback => {
  const id = numberId(value, 'Кешбэк');
  const cashback = state.cashbacks.find((item) => item.id === id);
  if (!cashback) throw new SandboxDomainError('Правило кешбэка не найдено');
  return cashback;
};

const achievementById = (
  state: LoyaltyRuntime,
  value: unknown
): SandboxAchievement => {
  const id = numberId(value, 'Достижение');
  const achievement = state.achievements.find((item) => item.id === id);
  if (!achievement) throw new SandboxDomainError('Достижение не найдено');
  return achievement;
};

const promoCodeById = (
  state: LoyaltyRuntime,
  value: unknown
): SandboxPromoCode => {
  const id = numberId(value, 'Промокод');
  const promoCode = state.promoCodes.find((item) => item.id === id);
  if (!promoCode) throw new SandboxDomainError('Промокод не найден');
  return promoCode;
};

const promoCampaignById = (
  state: LoyaltyRuntime,
  value: unknown
): SandboxPromoCampaign => {
  const id = String(value ?? '');
  const campaign = state.promoCampaigns.find((item) => item.id === id);
  if (!campaign) throw new SandboxDomainError('Группа промокодов не найдена');
  return campaign;
};

const schedulesValue = (
  value: unknown,
  fallback: SandboxDiscount['schedules']
): SandboxDiscount['schedules'] => {
  if (value === undefined) return fallback;
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const schedule = recordValue(item);
    return {
      start: Number(schedule.start ?? 0),
      end: Number(schedule.end ?? 0),
    };
  });
};

const updateDiscount = (
  discount: SandboxDiscount,
  input: Record<string, unknown>
): SandboxDiscount => {
  if (input.title !== undefined) discount.title = titleValue(input.title);
  if (input.value !== undefined) discount.value = Number(input.value);
  if (input.active !== undefined) discount.active = Boolean(input.active);
  if (input.is_tracked !== undefined)
    discount.is_tracked = Boolean(input.is_tracked);
  if (input.dont_use_on_holidays !== undefined)
    discount.dont_use_on_holidays = Boolean(input.dont_use_on_holidays);
  discount.schedules = schedulesValue(input.schedules, discount.schedules);
  return discount;
};

const cashbackInput = (
  cashback: SandboxCashback,
  input: Record<string, unknown>
): SandboxCashback => {
  if (input.amount !== undefined) cashback.amount = Number(input.amount);
  if (input.value !== undefined) cashback.value = Number(input.value);
  if (input.is_percent !== undefined)
    cashback.is_percent = Boolean(input.is_percent);
  if (input.is_active !== undefined)
    cashback.is_active = Boolean(input.is_active);
  if (input.starts_at !== undefined)
    cashback.starts_at = input.starts_at ? String(input.starts_at) : undefined;
  if (input.expires_at !== undefined)
    cashback.expires_at = input.expires_at
      ? String(input.expires_at)
      : undefined;
  return cashback;
};

const applicableParams = (value: unknown, fallback: string): string => {
  if (value === undefined) return fallback;
  if (!value) {
    return JSON.stringify({
      goods: { all: true, items: [] },
      services: { all: true, items: [] },
      tariffs: { all: true, items: [] },
    });
  }

  const source = recordValue(value);
  const result: Record<string, { all: boolean; items: unknown[] }> = {};
  (['goods', 'services', 'tariffs'] as const).forEach((type) => {
    const ids = Array.isArray(source[type]) ? (source[type] as unknown[]) : [];
    const all = ids.some((id) => Number(id) === -1);
    result[type] = {
      all,
      items: all
        ? []
        : ids.map((id) => ({ id: Number(id), title: `Позиция ${Number(id)}` })),
    };
  });
  return JSON.stringify(result);
};

const promoClientScope = (
  value: unknown,
  store: SandboxStore,
  fallback: SandboxPromoClientScope[] | null
): SandboxPromoClientScope[] | null => {
  if (value === undefined) return fallback;
  if (!value) return null;
  const source = recordValue(value);
  const users = Array.isArray(source.users) ? source.users : [];
  const groups = Array.isArray(source.groups) ? source.groups : [];
  return [
    ...users.map((uuid) => {
      const client = store
        .getState()
        .clients.find((item) => item.uuid === String(uuid));
      return {
        __typename: 'ScopedClient' as const,
        id: String(uuid),
        avatar_url: client?.avatar_url ?? null,
        phone: client?.phone ?? 'Клиент песочницы',
      };
    }),
    ...groups.map((id) => ({
      __typename: 'ScopedGroup' as const,
      id: String(id),
      title: `Группа ${String(id)}`,
    })),
  ];
};

const promoValue = (
  value: unknown,
  fallback: SandboxPromoValue
): SandboxPromoValue => {
  if (value === undefined) return fallback;
  const source = recordValue(value);
  const type = String(
    source.type ?? fallback.type
  ) as SandboxPromoValue['type'];
  if (!['DISCOUNT', 'DEPOSIT', 'BONUS'].includes(type)) {
    throw new SandboxDomainError('Неизвестный тип вознаграждения');
  }
  return {
    currency: { alias: 'RUB', letter: '₽' },
    type,
    value: Number(source.value ?? fallback.value),
  };
};

const scopesValue = (
  value: unknown,
  fallback: SandboxPromoCode['scopes']
): SandboxPromoCode['scopes'] =>
  value === undefined
    ? fallback
    : Array.isArray(value)
    ? (value.map(String) as SandboxPromoCode['scopes'])
    : [];

const dateValue = (value: unknown, fallback: string | null): string | null =>
  value === undefined ? fallback : value ? String(value) : null;

const updatePromoCode = (
  promoCode: SandboxPromoCode,
  input: Record<string, unknown>,
  store: SandboxStore
): SandboxPromoCode => {
  if (input.code !== undefined)
    promoCode.code = String(input.code).trim().toUpperCase();
  if (input.amount !== undefined) promoCode.amount = Number(input.amount);
  if (input.active !== undefined) promoCode.active = Boolean(input.active);
  if (input.is_tracked !== undefined)
    promoCode.is_tracked = Boolean(input.is_tracked);
  promoCode.active_from = dateValue(input.active_from, promoCode.active_from);
  promoCode.active_to = dateValue(input.active_to, promoCode.active_to);
  promoCode.value = promoValue(input.value, promoCode.value);
  promoCode.applicable_params = applicableParams(
    input.applicable_params,
    promoCode.applicable_params
  );
  promoCode.client_scope = promoClientScope(
    input.client_scope,
    store,
    promoCode.client_scope
  );
  promoCode.scopes = scopesValue(input.scopes, promoCode.scopes);
  return promoCode;
};

const updatePromoCampaign = (
  campaign: SandboxPromoCampaign,
  input: Record<string, unknown>,
  store: SandboxStore
): SandboxPromoCampaign => {
  if (input.title !== undefined) campaign.title = titleValue(input.title);
  if (input.description !== undefined)
    campaign.description = String(input.description ?? '');
  if (input.active !== undefined) campaign.active = Boolean(input.active);
  if (input.is_tracked !== undefined)
    campaign.is_tracked = Boolean(input.is_tracked);
  campaign.active_from = dateValue(input.active_from, campaign.active_from);
  campaign.active_to = dateValue(input.active_to, campaign.active_to);
  campaign.value = promoValue(input.value, campaign.value);
  campaign.applicable_params = applicableParams(
    input.applicable_params,
    campaign.applicable_params
  );
  campaign.client_scope =
    promoClientScope(input.client_scope, store, campaign.client_scope) ?? [];
  campaign.scopes = scopesValue(input.scopes, campaign.scopes);

  if (input.generation_settings !== undefined) {
    const generation = recordValue(input.generation_settings);
    campaign.generation_settings = {
      length: Number(generation.length ?? campaign.generation_settings.length),
      prefix: String(generation.prefix ?? campaign.generation_settings.prefix),
      type: String(
        generation.type ?? campaign.generation_settings.type
      ) as SandboxPromoCampaign['generation_settings']['type'],
    };
    if (generation.amount !== undefined) {
      campaign.counters.total = Number(generation.amount);
    }
  }
  campaign.updated_at = store.getCurrentTimestamp();
  return campaign;
};

const paginatedItems = <T>(
  items: T[],
  variables: SandboxVariables
): { items: T[]; paginatorInfo: Record<string, number> } => {
  const count = Math.max(1, Number(variables.count ?? 10));
  const currentPage = Math.max(1, Number(variables.page ?? 1));
  const start = (currentPage - 1) * count;
  const pageItems = items.slice(start, start + count);
  return {
    items: pageItems,
    paginatorInfo: {
      count: pageItems.length,
      currentPage,
      lastPage: Math.max(1, Math.ceil(items.length / count)),
      total: items.length,
    },
  };
};

const searchQuery = (variables: SandboxVariables): string =>
  String(recordValue(variables.filter).q ?? '')
    .trim()
    .toLowerCase();

export const loyaltyResolvers: Record<string, SandboxOperationHandler> = {
  discounts: (_, store) => ({
    discounts: { data: runtime(store).discounts },
  }),
  createDiscount: (variables, store) => {
    const state = runtime(store);
    const discount: SandboxDiscount = {
      __typename: 'Discount',
      origin: 'runtime',
      id: state.nextDiscountId++,
      title: 'Новая скидка',
      value: 1,
      entity: 'TARIFF',
      is_tracked: false,
      dont_use_on_holidays: false,
      schedules: [],
      active: true,
    };
    updateDiscount(discount, inputObject(variables));
    state.discounts.push(discount);
    return { createDiscount: discount };
  },
  UpdateDiscount: (variables, store) => ({
    updateDiscount: updateDiscount(
      discountById(runtime(store), variables.updateDiscountId),
      inputObject(variables)
    ),
  }),
  deleteDiscount: (variables, store) => {
    const state = runtime(store);
    const discount = discountById(state, variables.id);
    assertRuntimeDelete(discount);
    state.discounts = state.discounts.filter((item) => item.id !== discount.id);
    return { deleteDiscount: discount };
  },
  depositCashbacks: (_, store) => ({
    depositCashbacks: runtime(store).cashbacks,
  }),
  createDepositCashback: (variables, store) => {
    const state = runtime(store);
    const input = inputObject(variables);
    const amount = Number(input.amount);
    if (state.cashbacks.some((item) => item.amount === amount)) {
      throw new SandboxDomainError('deposit cashback threshold is taken');
    }
    const cashback: SandboxCashback = {
      __typename: 'DepositCashback',
      origin: 'runtime',
      id: state.nextCashbackId++,
      amount,
      value: 1,
      is_percent: true,
      is_active: true,
    };
    cashbackInput(cashback, input);
    state.cashbacks.push(cashback);
    return { createDepositCashback: cashback };
  },
  updateDepositCashback: (variables, store) => ({
    updateDepositCashback: cashbackInput(
      cashbackById(runtime(store), variables.id),
      inputObject(variables)
    ),
  }),
  deleteDepositCashback: (variables, store) => {
    const state = runtime(store);
    const cashback = cashbackById(state, variables.id);
    assertRuntimeDelete(cashback);
    state.cashbacks = state.cashbacks.filter((item) => item.id !== cashback.id);
    return { deleteDepositCashback: cashback };
  },
  achievements: (_, store) => ({ achievements: runtime(store).achievements }),
  createAchievement: (variables, store) => {
    const state = runtime(store);
    const input = inputObject(variables);
    const achievement: SandboxAchievement = {
      __typename: 'Achievement',
      origin: 'runtime',
      id: state.nextAchievementId++,
      name: titleValue(input.name),
      created_at: store.getCurrentTimestamp(),
      assigned_count: 0,
      conditions: Array.isArray(input.conditions)
        ? (input.conditions as Array<{ name: string; value: number }>)
        : [],
      rewards: Array.isArray(input.rewards)
        ? (input.rewards as Array<{ name: string; value: number }>)
        : [],
    };
    state.achievements.push(achievement);
    return { createAchievement: achievement };
  },
  deleteAchievement: (variables, store) => {
    const state = runtime(store);
    const achievement = achievementById(state, variables.id);
    assertRuntimeDelete(achievement);
    state.achievements = state.achievements.filter(
      (item) => item.id !== achievement.id
    );
    return { deleteAchievement: achievement };
  },
  PromoCodesListV2: (variables, store) => {
    const query = searchQuery(variables);
    const items = runtime(store).promoCodes.filter(
      ({ code }) => !query || code.toLowerCase().includes(query)
    );
    return { promoCodesList: paginatedItems(items, variables) };
  },
  promoCampaignListV2: (variables, store) => {
    const query = searchQuery(variables);
    const items = runtime(store).promoCampaigns.filter(
      ({ title }) => !query || title.toLowerCase().includes(query)
    );
    return { promoCampaignList: paginatedItems(items, variables) };
  },
  PromoCodeV2: (variables, store) => ({
    promoCode: promoCodeById(runtime(store), variables.promoCodeId),
  }),
  PromoCampaignV2: (variables, store) => ({
    promoCampaign: promoCampaignById(runtime(store), variables.promoCampaignId),
  }),
  CreatePromoCodeV2: (variables, store) => {
    const state = runtime(store);
    const input = inputObject(variables);
    const promoCode: SandboxPromoCode = {
      __typename: 'PromoCodeEntity',
      origin: 'runtime',
      id: state.nextPromoCodeId++,
      active: true,
      active_from: null,
      active_to: null,
      amount: 1,
      counters: { used: 0 },
      applicable_params: applicableParams(null, ''),
      client_scope: null,
      code: `DEMO${state.nextPromoCodeId}`,
      created_at: store.getCurrentTimestamp(),
      is_tracked: false,
      scopes: ['BILLING'],
      value: {
        currency: { alias: 'RUB', letter: '₽' },
        type: 'DISCOUNT',
        value: 1,
      },
    };
    updatePromoCode(promoCode, input, store);
    state.promoCodes.push(promoCode);
    return { createPromoCode: promoCode };
  },
  UpdatePromoCodeV2: (variables, store) => ({
    updatePromoCode: updatePromoCode(
      promoCodeById(runtime(store), variables.id),
      inputObject(variables),
      store
    ),
  }),
  DeletePromoCodeV2: (variables, store) => {
    const state = runtime(store);
    const promoCode = promoCodeById(state, variables.id);
    assertRuntimeDelete(promoCode);
    state.promoCodes = state.promoCodes.filter(
      (item) => item.id !== promoCode.id
    );
    return { deletePromoCode: promoCode };
  },
  CreatePromoCampaignV2: (variables, store) => {
    const state = runtime(store);
    const input = inputObject(variables);
    const campaign: SandboxPromoCampaign = {
      __typename: 'PromoCampaign',
      origin: 'runtime',
      id: `runtime:promo-campaign:${state.nextPromoCampaignId++}`,
      title: 'Новая группа',
      description: '',
      value: {
        currency: { alias: 'RUB', letter: '₽' },
        type: 'BONUS',
        value: 1,
      },
      applicable_params: applicableParams(null, ''),
      client_scope: [],
      scopes: ['SHELL'],
      generation_settings: { length: 8, prefix: 'DEMO', type: 'MIXED' },
      counters: { total: 1, used: 0 },
      active: true,
      active_from: null,
      active_to: null,
      is_tracked: false,
      created_at: store.getCurrentTimestamp(),
      updated_at: store.getCurrentTimestamp(),
    };
    updatePromoCampaign(campaign, input, store);
    state.promoCampaigns.push(campaign);
    return { createPromoCampaign: campaign };
  },
  UpdatePromoCampaignV2: (variables, store) => ({
    updatePromoCampaign: updatePromoCampaign(
      promoCampaignById(runtime(store), variables.id),
      inputObject(variables),
      store
    ),
  }),
  DeletePromoCampaignV2: (variables, store) => {
    const state = runtime(store);
    const campaign = promoCampaignById(state, variables.id);
    assertRuntimeDelete(campaign);
    state.promoCampaigns = state.promoCampaigns.filter(
      (item) => item.id !== campaign.id
    );
    return { deletePromoCampaign: campaign };
  },
  GeneratePromoCodesV2: (variables, store) => {
    const campaign = promoCampaignById(runtime(store), variables.id);
    campaign.counters.total += Math.max(0, Number(variables.amount ?? 0));
    campaign.updated_at = store.getCurrentTimestamp();
    return { generatePromoCodes: { id: campaign.id } };
  },
  promoCampaignCodesToCsvV2: (variables, store) => {
    promoCampaignById(runtime(store), variables.promoCampaignCodesToCsvId);
    return { promoCampaignCodesToCsv: 'data:text/csv;charset=utf-8,code' };
  },
};
